/**
 * AllocationFormPage — ERP-grade allocation & settlement screen
 * Handles both create (/new) and view/detail (/:id) modes.
 * Detects partyType (Customer | Supplier) from URL path.
 *
 * Layout (create mode):
 *   ┌──────────────────────────────────────────────────────┐
 *   │ Header bar: back, title, status, action buttons      │
 *   ├──────────────────────────────────────────────────────┤
 *   │ Header fields: party, date, type, currency, narration│
 *   ├─────────────────────┬────────────────────────────────┤
 *   │ CREDITS (left)      │ DEBITS / INVOICES (right)      │
 *   │ Receipts/Payments   │ Invoices/Bills — amount inputs │
 *   ├─────────────────────┴────────────────────────────────┤
 *   │ Footer totals: Available | Allocated | Remaining      │
 *   └──────────────────────────────────────────────────────┘
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  ArrowLeft, CheckCircle2, RotateCcw, Save, Zap, Search,
  AlertTriangle, Info, FileText, ChevronDown, X, Loader2,
  SendHorizontal, ThumbsUp, Ban, Download,
} from 'lucide-react';

import { allocationApi } from '@/api/allocation.api';
import { openItemApi }   from '@/api/openItem.api';
import { customerApi }   from '@/api/customer.api';
import { supplierApi }   from '@/api/supplier.api';
import { currencyApi }   from '@/api/currency.api';
import type { CurrencyListDto } from '@/types/currency.types';
import { exportToCsv }   from '@/utils/exportCsv';
import { usePermission } from '@/hooks/usePermission';
import { Permissions }   from '@/constants/permissions';
import { AppRoutes }     from '@/constants/routes';
import { Spinner }       from '@/components/ui/Spinner';
import { DocumentReferences } from '@/components/fin/DocumentReferences';
import { cn }            from '@/utils/cn';
import type { RootState } from '@/state/store';
import type {
  AllocationHeaderDto,
  AllocationWorkflowStatus,
  AllocationType,
  CreateAllocationRequest,
} from '@/types/allocation.types';
import type { OpenItemDto, OpenItemType } from '@/types/openItem.types';

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
const today = () => new Date().toISOString().slice(0, 10);

// ── Document type classification ──────────────────────────────────────────────

const AR_CREDIT_TYPES  = new Set<OpenItemType>(['CustomerReceipt', 'ArCreditNote', 'AdvanceReceipt']);
const AR_DEBIT_TYPES   = new Set<OpenItemType>(['ArInvoice', 'ArDebitNote', 'OpeningBalance']);
const AP_CREDIT_TYPES  = new Set<OpenItemType>(['SupplierPayment', 'ApCreditNote', 'AdvancePayment']);
const AP_DEBIT_TYPES   = new Set<OpenItemType>(['ApBill', 'ApDebitNote', 'OpeningBalance']);

const ALLOCATION_TYPES_AR: AllocationType[] = [
  'CustomerReceiptAllocation', 'CreditNoteAllocation', 'AdvanceAllocation', 'ManualAllocation',
];
const ALLOCATION_TYPES_AP: AllocationType[] = [
  'SupplierPaymentAllocation', 'CreditNoteAllocation', 'AdvanceAllocation', 'ManualAllocation',
];

const TYPE_LABEL: Partial<Record<AllocationType, string>> = {
  CustomerReceiptAllocation: 'Receipt Allocation',
  SupplierPaymentAllocation: 'Payment Allocation',
  CreditNoteAllocation:      'Credit Note Allocation',
  DebitNoteAllocation:       'Debit Note Allocation',
  AdvanceAllocation:         'Advance Allocation',
  ManualAllocation:          'Manual Allocation',
  AutoAllocation:            'Auto Allocation',
  Reallocation:              'Reallocation',
};

const STATUS_COLORS: Record<AllocationWorkflowStatus, string> = {
  Draft:     'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400',
  Submitted: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  Approved:  'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  Confirmed: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
  Reversed:  'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
  Cancelled: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
};

// ── Party search result ───────────────────────────────────────────────────────

interface PartyOption {
  id:        string; // = partyId
  partyId:   string;
  partyCode: string;
  partyName: string;
}

// ── Main component ────────────────────────────────────────────────────────────

export const AllocationFormPage = () => {
  const navigate        = useNavigate();
  const location        = useLocation();
  const { id }          = useParams<{ id: string }>();
  const { hasPermission } = usePermission();
  const session         = useSelector((s: RootState) => s.session.session);

  // Detect module from URL path
  const isAR      = location.pathname.startsWith('/ar/');
  const partyType = isAR ? 'Customer' : 'Supplier';
  const isNew     = !id;

  // Permissions
  const canCreate  = hasPermission(isAR ? Permissions.AR_ALLOCATION_CREATE  : Permissions.AP_ALLOCATION_CREATE);
  const canEdit    = hasPermission(isAR ? Permissions.AR_ALLOCATION_EDIT    : Permissions.AP_ALLOCATION_EDIT);
  const canConfirm = hasPermission(isAR ? Permissions.AR_ALLOCATION_CONFIRM : Permissions.AP_ALLOCATION_CONFIRM);
  const canReverse = hasPermission(isAR ? Permissions.AR_ALLOCATION_REVERSE : Permissions.AP_ALLOCATION_REVERSE)
                  || hasPermission(Permissions.ALLOCATION_REVERSE_CONFIRMED);

  const backRoute  = isAR ? AppRoutes.AR_ALLOCATION : AppRoutes.AP_ALLOCATION;
  const creditTypes = isAR ? AR_CREDIT_TYPES : AP_CREDIT_TYPES;
  const debitTypes  = isAR ? AR_DEBIT_TYPES  : AP_DEBIT_TYPES;
  const allocationTypeOptions = isAR ? ALLOCATION_TYPES_AR : ALLOCATION_TYPES_AP;

  // ── View mode state (existing allocation) ─────────────────────────────────

  const [existing, setExisting] = useState<AllocationHeaderDto | null>(null);
  const [loadingExisting, setLoadingExisting] = useState(false);

  // ── Create mode state ─────────────────────────────────────────────────────

  // Party search
  const [partySearch,      setPartySearch]      = useState('');
  const [partyOptions,     setPartyOptions]      = useState<PartyOption[]>([]);
  const [partySearching,   setPartySearching]    = useState(false);
  const [showPartyDropdown,setShowPartyDropdown] = useState(false);
  const [selectedParty,    setSelectedParty]     = useState<PartyOption | null>(null);

  // Header fields
  const [allocDate,   setAllocDate]   = useState(today());
  const [allocType,   setAllocType]   = useState<AllocationType>(allocationTypeOptions[0]);
  const [currency,    setCurrency]    = useState(session?.companyCurrency ?? 'USD');
  const [currencies,  setCurrencies]  = useState<CurrencyListDto[]>([]);
  const [narration,   setNarration]   = useState('');
  const [reference,   setReference]  = useState('');

  // Active currencies only for the open-item filter (QA #1).
  useEffect(() => {
    currencyApi.getList({ pageSize: 100, isActive: true })
      .then(res => setCurrencies(res.data.data?.items ?? []))
      .catch(() => { /* leaves the default the only option */ });
  }, []);

  // Open items
  const [openItems,     setOpenItems]    = useState<OpenItemDto[]>([]);
  const [loadingItems,  setLoadingItems] = useState(false);

  // Allocation amounts: map of openItemId → amount
  const [creditSel, setCreditSel] = useState<Record<string, boolean>>({});  // selected credit items
  const [debitAmts, setDebitAmts] = useState<Record<string, string>>({});   // editable debit amounts

  // Submission
  const [submitting,    setSubmitting]    = useState(false);
  const [submitError,   setSubmitError]   = useState<string | null>(null);
  const [reverseReason, setReverseReason] = useState('');
  const [showReverse,   setShowReverse]   = useState(false);
  const [confirmMode,   setConfirmMode]   = useState(false);

  // ── Derived lists ─────────────────────────────────────────────────────────

  const credits = useMemo(() =>
    openItems.filter(i => creditTypes.has(i.documentType as OpenItemType) && i.outstandingAmount > 0),
    [openItems, creditTypes]);

  const debits = useMemo(() =>
    openItems.filter(i => debitTypes.has(i.documentType as OpenItemType) && i.outstandingAmount > 0)
             .sort((a, b) => {
               // FIFO: sort by due date then document date
               const da = a.dueDate ?? a.documentDate;
               const db = b.dueDate ?? b.documentDate;
               return da.localeCompare(db);
             }),
    [openItems, debitTypes]);

  // ── Load existing allocation (view mode) ──────────────────────────────────

  useEffect(() => {
    if (!isNew && id) {
      setLoadingExisting(true);
      allocationApi.getById(id)
        .then(res => setExisting(res.data?.data ?? null))
        .catch(e => console.error('Load allocation failed', e))
        .finally(() => setLoadingExisting(false));
    }
  }, [id, isNew]);

  // ── Party search ──────────────────────────────────────────────────────────

  const searchParties = useCallback(async (q: string) => {
    if (!q.trim()) { setPartyOptions([]); return; }
    setPartySearching(true);
    try {
      // Search the party master server-side (QA #42). The previous code pulled the first 50 open
      // items and filtered in the browser, so the query never reached the server and a party past
      // that first page — or one with no open items yet — could never be found.
      const res = partyType === 'Customer'
        ? await customerApi.getList({ search: q.trim(), isActive: true, pageNumber: 1, pageSize: 20 })
        : await supplierApi.getList({ search: q.trim(), isActive: true, pageNumber: 1, pageSize: 20 });
      const opts: PartyOption[] = (res.data?.data?.items ?? []).map(p => ({
        id: p.id, partyId: p.id, partyCode: p.code, partyName: p.name,
      }));
      setPartyOptions(opts);
    } catch (e) {
      console.error('Party search failed', e);
    } finally {
      setPartySearching(false);
    }
  }, [partyType]);

  useEffect(() => {
    const t = setTimeout(() => searchParties(partySearch), 300);
    return () => clearTimeout(t);
  }, [partySearch, searchParties]);

  // ── Load open items for selected party ───────────────────────────────────

  const loadOpenItems = useCallback(async (party: PartyOption) => {
    setLoadingItems(true);
    setOpenItems([]);
    setCreditSel({});
    setDebitAmts({});
    try {
      const res = await openItemApi.getByParty({
        partyType,
        partyId:     party.partyId,
        currencyCode: currency || undefined,
        locationId:   session?.locationId   || undefined,
        costCenterId: session?.costCenterId || undefined,
      });
      const items = (res.data?.data ?? []).filter(i =>
        i.outstandingAmount > 0 &&
        (i.status === 'Open' || i.status === 'PartiallyAllocated')
      );
      setOpenItems(items);
      // Pre-select all credit items
      const sel: Record<string, boolean> = {};
      items.forEach(i => {
        if (creditTypes.has(i.documentType as OpenItemType) && i.outstandingAmount > 0)
          sel[i.id] = true;
      });
      setCreditSel(sel);
    } catch (e) {
      console.error('Load open items failed', e);
    } finally {
      setLoadingItems(false);
    }
  }, [partyType, currency, session, creditTypes]);

  const handleSelectParty = (p: PartyOption) => {
    setSelectedParty(p);
    setPartySearch(p.partyCode + ' — ' + p.partyName);
    setPartyOptions([]);
    setShowPartyDropdown(false);
    loadOpenItems(p);
  };

  // ── Totals ────────────────────────────────────────────────────────────────

  const totalCreditAvailable = useMemo(() =>
    credits.filter(c => creditSel[c.id]).reduce((s, c) => s + c.outstandingAmount, 0),
    [credits, creditSel]);

  /**
   * Decide how much of `amount` comes off each selected credit, oldest first.
   *
   * Ticking two receipts to settle one invoice does not say how much comes off each — the screen
   * has to answer that, and the server needs the answer as explicit lines. Oldest-first matches
   * the order the FIFO helper proposes and the order a clerk would apply by hand.
   *
   * Never draws more than a credit has outstanding, and never more than `amount` in total, so the
   * lines it returns sum to exactly `amount` whenever the selected credits can cover it.
   */
  const apportionAcrossCredits = (
    selected: OpenItemDto[],
    amount: number,
  ): { openItemId: string; allocatedAmount: number }[] => {
    const byOldest = [...selected].sort((a, b) => a.documentDate.localeCompare(b.documentDate));
    const lines: { openItemId: string; allocatedAmount: number }[] = [];
    let left = amount;

    for (const c of byOldest) {
      if (left <= 0.001) break;
      // Round each draw to 2dp so the lines sum to the cent the server will check against.
      const take = Math.round(Math.min(c.outstandingAmount, left) * 100) / 100;
      if (take > 0) {
        lines.push({ openItemId: c.id, allocatedAmount: take });
        left = Math.round((left - take) * 100) / 100;
      }
    }
    return lines;
  };

  const totalAllocated = useMemo(() =>
    debits.reduce((s, d) => s + (parseFloat(debitAmts[d.id] || '0') || 0), 0),
    [debits, debitAmts]);

  const remaining = totalCreditAvailable - totalAllocated;

  /**
   * Why this allocation cannot be saved yet, in words the person looking at the screen can act on
   * — or null when it can. Drives the buttons AND the label beside them, so the two can never
   * disagree about whether saving is possible.
   *
   * Mirrors the server's rule (CreateAllocationCommandHandler: credits must equal debits) rather
   * than replacing it. The server stays authoritative for API, import and integration callers;
   * this exists so a human is never sent to a dead end and told about control accounts.
   */
  const blockReason = useMemo<string | null>(() => {
    if (!selectedParty) return null;                       // nothing chosen yet — not an error
    const noun     = isAR ? 'invoice' : 'bill';
    const credNoun = isAR ? 'receipts' : 'payments';

    if (credits.length === 0)
      return `${selectedParty.partyName} has no unallocated ${credNoun} to allocate from.`;
    if (debits.length === 0)
      return `${selectedParty.partyName} has no outstanding ${noun}s to allocate to.`;
    if (totalAllocated <= 0)
      return `Enter an amount against at least one ${noun}.`;
    if (totalCreditAvailable <= 0)
      return `Select the ${credNoun.slice(0, -1)} this allocation draws from.`;
    if (totalAllocated > totalCreditAvailable + 0.001)
      return `Applying ${fmt(totalAllocated)} but only ${fmt(totalCreditAvailable)} selected — `
           + `${fmt(totalAllocated - totalCreditAvailable)} unfunded.`;
    return null;
  }, [selectedParty, credits, debits, totalAllocated, totalCreditAvailable, isAR]);

  // ── FIFO auto-allocate ────────────────────────────────────────────────────

  const handleFifo = () => {
    // Debits are already sorted by due date (oldest first) in the `debits` memo
    // Credits sorted by document date (oldest first)
    const availableCredits = credits
      .filter(c => creditSel[c.id])
      .map(c => ({ id: c.id, remaining: c.outstandingAmount }))
      .sort((a, b) => {
        const ca = credits.find(c => c.id === a.id)!;
        const cb = credits.find(c => c.id === b.id)!;
        return ca.documentDate.localeCompare(cb.documentDate);
      });

    let totalCredit = availableCredits.reduce((s, c) => s + c.remaining, 0);
    const newAmts: Record<string, string> = {};

    for (const debit of debits) {
      if (totalCredit <= 0) break;
      const apply = Math.min(debit.outstandingAmount, totalCredit);
      if (apply > 0) {
        newAmts[debit.id] = apply.toFixed(2);
        totalCredit -= apply;
      }
    }
    setDebitAmts(newAmts);
  };

  const handleClearAmounts = () => setDebitAmts({});

  // ── Submit (create) ───────────────────────────────────────────────────────

  const handleSave = async (confirmImmediately: boolean) => {
    if (!selectedParty || !session) return;

    const debitLines = debits
      .filter(d => (parseFloat(debitAmts[d.id] || '0') || 0) > 0)
      .map(d => ({
        openItemId:      d.id,
        allocatedAmount: parseFloat(debitAmts[d.id] || '0') || 0,
      }));

    if (debitLines.length === 0) {
      setSubmitError(`Enter an amount against at least one ${isAR ? 'invoice' : 'bill'}.`);
      return;
    }

    // An allocation has TWO sides. The screen only ever sent the invoices, so the server saw
    // nothing coming off a receipt and refused every allocation as unbalanced — "0.00 allocated
    // from receipts/payments against N applied to invoices/bills". That message was correct and
    // literal: the credit side was never in the request. Selecting a credit only ever tinted a
    // row and fed a client-side total.
    //
    // Drawing FROM the credits is a decision the screen has to make, because ticking two receipts
    // to settle one invoice does not say how much comes off each. Consume them oldest-first, the
    // same order the FIFO helper proposes, so what the user is shown is what gets sent.
    const creditLines = apportionAcrossCredits(
      credits.filter(c => creditSel[c.id]),
      totalAllocated,
    );

    const shortfall = totalAllocated - creditLines.reduce((s, l) => s + l.allocatedAmount, 0);
    if (shortfall > 0.001) {
      // Defence in depth: the button is disabled in this state, so reaching here means a
      // rounding path we have not thought of. Say what is wrong, not that something went wrong.
      setSubmitError(
        `${fmt(totalAllocated)} is being applied but only ${fmt(totalCreditAvailable)} of credit `
        + `is selected — ${fmt(shortfall)} is unfunded. Select more ${isAR ? 'receipts' : 'payments'} `
        + `or reduce the amounts applied.`);
      return;
    }

    const lines = [...creditLines, ...debitLines];

    const req: CreateAllocationRequest = {
      companyId:           session.companyId,
      allocationDate:      allocDate,
      financialYearCode:   session.financialYearCode  ?? '',
      financialPeriodCode: session.financialPeriodCode ?? '',
      locationId:          session.locationId   || null,
      costCenterId:        session.costCenterId || null,
      partyType,
      partyId:    selectedParty.partyId,
      partyCode:  selectedParty.partyCode,
      partyName:  selectedParty.partyName,
      currencyCode: currency,
      exchangeRate: 1,
      allocationType: allocType,
      referenceNumber: reference || null,
      narration:       narration || null,
      confirmImmediately,
      lines,
    };

    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await allocationApi.create(req);
      const newId = res.data?.data;
      if (newId) {
        const detailRoute = isAR
          ? AppRoutes.AR_ALLOCATION_DETAIL.replace(':id', newId)
          : AppRoutes.AP_ALLOCATION_DETAIL.replace(':id', newId);
        navigate(detailRoute);
      }
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setSubmitError(msg ?? 'Failed to save allocation.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Submit existing (view mode) ───────────────────────────────────────────

  const handleSubmit = async () => {
    if (!existing) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await allocationApi.submit(existing.id);
      const res = await allocationApi.getById(existing.id);
      setExisting(res.data?.data ?? null);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setSubmitError(msg ?? 'Failed to submit allocation.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Approve existing (view mode) ──────────────────────────────────────────

  const handleApprove = async () => {
    if (!existing) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await allocationApi.approve(existing.id);
      const res = await allocationApi.getById(existing.id);
      setExisting(res.data?.data ?? null);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setSubmitError(msg ?? 'Failed to approve allocation.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Cancel existing (view mode) ───────────────────────────────────────────

  const handleCancel = async () => {
    if (!existing) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await allocationApi.cancel(existing.id);
      const res = await allocationApi.getById(existing.id);
      setExisting(res.data?.data ?? null);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setSubmitError(msg ?? 'Failed to cancel allocation.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Confirm existing (view mode) ──────────────────────────────────────────

  const handleConfirm = async () => {
    if (!existing) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await allocationApi.confirm(existing.id);
      const res = await allocationApi.getById(existing.id);
      setExisting(res.data?.data ?? null);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setSubmitError(msg ?? 'Failed to confirm allocation.');
    } finally {
      setSubmitting(false);
      setConfirmMode(false);
    }
  };

  // ── Reverse existing (view mode) ──────────────────────────────────────────

  const handleReverse = async () => {
    if (!existing || !reverseReason.trim()) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await allocationApi.reverse(existing.id, reverseReason.trim());
      const newReversalId = res.data?.data;
      if (newReversalId) {
        const route = isAR
          ? AppRoutes.AR_ALLOCATION_DETAIL.replace(':id', newReversalId)
          : AppRoutes.AP_ALLOCATION_DETAIL.replace(':id', newReversalId);
        navigate(route);
      }
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setSubmitError(msg ?? 'Failed to reverse allocation.');
    } finally {
      setSubmitting(false);
      setShowReverse(false);
    }
  };

  // ── Render helpers ────────────────────────────────────────────────────────

  const pageTitle = isAR ? 'Customer Allocation' : 'Supplier Allocation';

  // ── VIEW MODE ─────────────────────────────────────────────────────────────

  if (!isNew) {
    if (loadingExisting) {
      return (
        <div className="flex items-center justify-center h-full">
          <Spinner size="md" />
        </div>
      );
    }
    if (!existing) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-slate-400">
          <FileText size={40} className="mb-3 opacity-30" />
          <p className="text-sm">Allocation not found.</p>
          <button onClick={() => navigate(backRoute)} className="mt-3 text-sm text-blue-600 dark:text-blue-400 hover:underline">Back to list</button>
        </div>
      );
    }

    const status = existing.workflowStatus;
    const canSubmitThis  = canEdit    && status === 'Draft';
    const canApproveThis = canConfirm && status === 'Submitted';
    const canConfirmThis = canConfirm && (status === 'Draft' || status === 'Submitted' || status === 'Approved');
    const canCancelThis  = canEdit    && (status === 'Draft' || status === 'Submitted');
    const canReverseThis = canReverse && status === 'Confirmed';

    return (
      <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
        {/* Header bar */}
        <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate(backRoute)} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400">
                <ArrowLeft size={16} />
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-lg font-semibold text-slate-900 dark:text-white">{pageTitle}</h1>
                  <span className="font-mono text-sm text-blue-600 dark:text-blue-400">{existing.allocationNumber}</span>
                  {existing.isReversal && <span className="text-[10px] bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded-full">REVERSAL</span>}
                  <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full', STATUS_COLORS[status])}>{status}</span>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  {fmtDate(existing.allocationDate)} · {existing.partyName} ({existing.partyCode}) · {existing.currencyCode}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Export lines */}
              {existing.details.length > 0 && (
                <button
                  onClick={() => exportToCsv(`allocation-${existing.allocationNumber}.csv`,
                    existing.details.map(d => ({
                      Line: d.lineNumber, Document: d.documentNumber, Type: d.documentType,
                      Date: d.documentDate, DueDate: d.dueDate ?? '', Currency: d.currencyCode,
                      Original: d.originalAmount, Before: d.outstandingBefore,
                      Allocated: d.allocatedAmount, After: d.outstandingAfter,
                      Reference: d.allocationReference ?? '', Remarks: d.remarks ?? '',
                    }))
                  )}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-slate-600 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-300 rounded-lg transition-colors">
                  <Download size={14} /> Export
                </button>
              )}
              {/* Cancel */}
              {canCancelThis && (
                <button onClick={handleCancel} disabled={submitting}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 rounded-lg transition-colors">
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />} Cancel
                </button>
              )}
              {/* Submit (Draft → Submitted) */}
              {canSubmitThis && (
                <button onClick={handleSubmit} disabled={submitting}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 rounded-lg transition-colors">
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : <SendHorizontal size={14} />} Submit
                </button>
              )}
              {/* Approve (Submitted → Approved) */}
              {canApproveThis && (
                <button onClick={handleApprove} disabled={submitting}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-indigo-700 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 rounded-lg transition-colors">
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : <ThumbsUp size={14} />} Approve
                </button>
              )}
              {/* Reverse (Confirmed → Reversal) */}
              {canReverseThis && (
                <button onClick={() => setShowReverse(true)} disabled={submitting}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 rounded-lg transition-colors">
                  <RotateCcw size={14} /> Reverse
                </button>
              )}
              {/* Confirm */}
              {canConfirmThis && (
                <button onClick={() => setConfirmMode(true)} disabled={submitting}
                  className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors">
                  <CheckCircle2 size={14} /> Confirm
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Confirm prompt */}
        {confirmMode && (
          <div className="bg-emerald-50 dark:bg-emerald-900/20 border-b border-emerald-200 dark:border-emerald-800 px-6 py-3 flex items-center gap-4">
            <Info size={16} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
            <p className="text-sm text-emerald-800 dark:text-emerald-300 flex-1">
              Confirming this allocation will update outstanding balances. This action cannot be undone without a reversal.
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => setConfirmMode(false)} className="text-sm text-slate-600 dark:text-gray-400 hover:text-slate-800 px-3 py-1.5 rounded-lg hover:bg-slate-100">Cancel</button>
              <button onClick={handleConfirm} disabled={submitting}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg">
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                Confirm
              </button>
            </div>
          </div>
        )}

        {/* Error */}
        {submitError && (
          <div className="bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 px-6 py-3 flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
            <AlertTriangle size={14} /> {submitError}
          </div>
        )}

        {/* Allocation detail body */}
        <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
          {/* Header info grid */}
          <div className="bg-white dark:bg-slate-800 rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 p-4 grid grid-cols-3 gap-4 text-sm">
            <div><span className="text-[11px] text-slate-400 uppercase tracking-wider block">Type</span><span className="font-medium text-slate-900 dark:text-white">{TYPE_LABEL[existing.allocationType] ?? existing.allocationType}</span></div>
            <div><span className="text-[11px] text-slate-400 uppercase tracking-wider block">Party</span><span className="font-medium text-slate-900 dark:text-white">{existing.partyName}</span><span className="text-xs text-slate-400"> ({existing.partyCode})</span></div>
            <div><span className="text-[11px] text-slate-400 uppercase tracking-wider block">Currency</span><span className="font-medium text-slate-900 dark:text-white">{existing.currencyCode} @ {existing.exchangeRate.toFixed(4)}</span></div>
            {existing.referenceNumber && <div><span className="text-[11px] text-slate-400 uppercase tracking-wider block">Reference</span><span className="font-medium text-slate-900 dark:text-white">{existing.referenceNumber}</span></div>}
            {existing.narration && <div className="col-span-2"><span className="text-[11px] text-slate-400 uppercase tracking-wider block">Narration</span><span className="text-slate-700 dark:text-slate-300">{existing.narration}</span></div>}
            {existing.confirmedByName && <div><span className="text-[11px] text-slate-400 uppercase tracking-wider block">Confirmed by</span><span className="text-slate-700 dark:text-slate-300">{existing.confirmedByName} on {fmtDate(existing.confirmedDate!)}</span></div>}
            {existing.reversedByName && <div><span className="text-[11px] text-slate-400 uppercase tracking-wider block">Reversed by</span><span className="text-slate-700 dark:text-slate-300">{existing.reversedByName} on {fmtDate(existing.reversedDate!)}</span></div>}
            {existing.reversalReason && <div className="col-span-2"><span className="text-[11px] text-slate-400 uppercase tracking-wider block">Reversal reason</span><span className="text-slate-700 dark:text-slate-300">{existing.reversalReason}</span></div>}
          </div>

          {/* Lines table */}
          <div className="bg-white dark:bg-slate-800 rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Allocation Lines</h2>
              <span className="text-xs text-slate-400">{existing.details.length} line{existing.details.length !== 1 ? 's' : ''}</span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-700/60 border-b border-slate-200 dark:border-slate-700 text-xs text-slate-500 dark:text-gray-400 uppercase tracking-wider">
                  {['#', 'Document', 'Date', 'Due Date', 'Original', 'Before', 'Allocated', 'After', 'Currency'].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left font-semibold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {existing.details.map(d => (
                  <tr key={d.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30">
                    <td className="px-4 py-2.5 text-xs text-slate-400">{d.lineNumber}</td>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-slate-900 dark:text-white text-xs">{d.documentNumber}</div>
                      <div className="text-[11px] text-slate-400">{d.documentType}</div>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-600 dark:text-slate-400 whitespace-nowrap">{fmtDate(d.documentDate)}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-600 dark:text-slate-400 whitespace-nowrap">{d.dueDate ? fmtDate(d.dueDate) : '—'}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs text-slate-700 dark:text-slate-300 whitespace-nowrap">{fmt(d.originalAmount)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs text-slate-700 dark:text-slate-300 whitespace-nowrap">{fmt(d.outstandingBefore)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs font-semibold text-emerald-700 dark:text-emerald-400 whitespace-nowrap">{fmt(d.allocatedAmount)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs text-slate-700 dark:text-slate-300 whitespace-nowrap">{fmt(d.outstandingAfter)}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500 dark:text-gray-400">{d.currencyCode}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 dark:bg-slate-700/40 border-t border-slate-200 dark:border-slate-700">
                  <td colSpan={6} className="px-4 py-2.5 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Total Allocated</td>
                  <td className="px-4 py-2.5 text-right font-mono text-sm font-bold text-emerald-700 dark:text-emerald-400">{fmt(existing.totalAllocated)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Reverse modal */}
        {showReverse && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-slate-900 dark:text-white">Reverse Allocation</h2>
                <button onClick={() => setShowReverse(false)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                A reversal allocation document will be created. The outstanding balances of all allocated items will be restored.
              </p>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Reason for reversal <span className="text-red-500">*</span></label>
              <textarea
                value={reverseReason} onChange={e => setReverseReason(e.target.value)}
                rows={3} placeholder="Enter reversal reason…"
                className="w-full border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white dark:bg-slate-700 focus:ring-2 focus:ring-amber-500 outline-none resize-none"
              />
              {submitError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{submitError}</p>}
              <div className="flex justify-end gap-2 mt-4">
                <button onClick={() => setShowReverse(false)} className="px-4 py-2 text-sm text-slate-600 dark:text-gray-400 hover:bg-slate-100 rounded-lg">Cancel</button>
                <button onClick={handleReverse} disabled={!reverseReason.trim() || submitting}
                  className="flex items-center gap-1.5 px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg">
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                  Reverse
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── CREATE MODE ───────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">

      {/* ── Top header bar ─────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(backRoute)} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400">
            <ArrowLeft size={16} />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-slate-900 dark:text-white">New {pageTitle}</h1>
            <p className="text-xs text-slate-500 dark:text-gray-400 mt-0.5">{session?.financialYearCode ?? '—'} · {session?.companyName ?? '—'}</p>
          </div>
        </div>
        {canCreate ? (
          <div className="flex items-center gap-2">
            {/* Why the reason sits next to the buttons rather than behind a failed save: an
                allocation that cannot balance cannot be saved in any state, draft included. The
                screen used to let you try and then relayed a control-account message written for
                developers. Say what is wrong here, before the click. */}
            {blockReason && (
              <span
                className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1.5 rounded-lg dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800"
                title={blockReason}
              >
                {blockReason}
              </span>
            )}
            <button onClick={() => handleSave(false)} disabled={submitting || !!blockReason}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-slate-700 dark:text-gray-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 rounded-lg disabled:opacity-40 transition-colors">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save Draft
            </button>
            <button onClick={() => handleSave(true)} disabled={submitting || !!blockReason}
              className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-sm font-medium rounded-lg transition-colors">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              Confirm
            </button>
          </div>
        ) : (
          <span className="text-xs text-red-500 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg">No create permission</span>
        )}
      </div>

      {/* ── Error banner ───────────────────────────────────────────────────── */}
      {submitError && (
        <div className="bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 px-6 py-3 flex items-center gap-2 text-sm text-red-700 dark:text-red-400 shrink-0">
          <AlertTriangle size={14} className="shrink-0" /> {submitError}
          <button onClick={() => setSubmitError(null)} className="ml-auto text-red-400 hover:text-red-600"><X size={14} /></button>
        </div>
      )}

      {/* ── Header fields ──────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4 shrink-0">
        <div className="grid grid-cols-6 gap-4 items-start">

          {/* Party search */}
          <div className="col-span-2 relative">
            <label className="block text-[11px] font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider mb-1">
              {isAR ? 'Customer' : 'Supplier'} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                value={partySearch}
                onChange={e => { setPartySearch(e.target.value); setShowPartyDropdown(true); setSelectedParty(null); }}
                onFocus={() => setShowPartyDropdown(true)}
                placeholder={`Search ${isAR ? 'customer' : 'supplier'}…`}
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
              />
              {partySearching && <Loader2 size={13} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />}
            </div>
            {showPartyDropdown && partyOptions.length > 0 && (
              <div className="absolute z-30 top-full mt-1 left-0 right-0 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {partyOptions.map(p => (
                  <button key={p.id} onMouseDown={() => handleSelectParty(p)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 dark:hover:bg-slate-700 text-sm">
                    <span className="font-mono text-blue-600 dark:text-blue-400 text-xs mr-2">{p.partyCode}</span>
                    <span className="text-slate-900 dark:text-white">{p.partyName}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Date */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider mb-1">Date</label>
            <input type="date" value={allocDate} onChange={e => setAllocDate(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" />
          </div>

          {/* Allocation type */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider mb-1">Type</label>
            <div className="relative">
              <select value={allocType} onChange={e => setAllocType(e.target.value as AllocationType)}
                className="w-full appearance-none px-3 pr-8 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none">
                {allocationTypeOptions.map(t => <option key={t} value={t}>{TYPE_LABEL[t] ?? t}</option>)}
              </select>
              <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>

          {/* Currency */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider mb-1">Currency</label>
            <select value={currency} onChange={e => setCurrency(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none">
              {currency && !currencies.some(c => c.code === currency) && <option value={currency}>{currency}</option>}
              {currencies.map(c => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
            </select>
          </div>

          {/* Reference */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider mb-1">Reference</label>
            <input value={reference} onChange={e => setReference(e.target.value)} placeholder="Optional…"
              className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" />
          </div>

          {/* Narration */}
          <div className="col-span-6">
            <label className="block text-[11px] font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider mb-1">Narration</label>
            <input value={narration} onChange={e => setNarration(e.target.value)} placeholder="Optional narration…"
              className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" />
          </div>
        </div>
      </div>

      {/* ── Split panel ────────────────────────────────────────────────────── */}
      {!selectedParty ? (
        <div className="flex-1 flex items-center justify-center text-slate-400">
          <div className="text-center">
            <Search size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">Select a {isAR ? 'customer' : 'supplier'} to view open items</p>
          </div>
        </div>
      ) : loadingItems ? (
        <div className="flex-1 flex items-center justify-center"><Spinner size="md" /></div>
      ) : (
        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="flex-1 overflow-hidden flex">

            {/* ── LEFT: Credits ─────────────────────────────────────────── */}
            <div className="w-80 shrink-0 border-r border-slate-200 dark:border-slate-700 flex flex-col bg-white dark:bg-slate-800">
              <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
                <h2 className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  {isAR ? 'Receipts / Credits' : 'Payments / Credits'}
                </h2>
                <span className="text-[11px] text-slate-400">{credits.length} items</span>
              </div>
              <div className="flex-1 overflow-y-auto divide-y divide-slate-50 dark:divide-slate-700">
                {credits.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-32 text-slate-400">
                    <FileText size={24} className="mb-2 opacity-30" />
                    <p className="text-xs">No available credits</p>
                  </div>
                ) : (
                  credits.map(c => (
                    <label key={c.id} className={cn(
                      'flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-blue-50 dark:hover:bg-slate-700/40 transition-colors',
                      creditSel[c.id] && 'bg-blue-50 dark:bg-blue-900/20',
                    )}>
                      <input type="checkbox" checked={!!creditSel[c.id]}
                        onChange={e => setCreditSel(s => ({ ...s, [c.id]: e.target.checked }))}
                        className="mt-0.5 accent-blue-600" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-xs text-blue-700 dark:text-blue-400 truncate">{c.documentNumber}</span>
                          <span className="text-[10px] text-slate-400 ml-1 shrink-0">{fmtDate(c.documentDate)}</span>
                        </div>
                        <div className="text-[10px] text-slate-400 mt-0.5">{c.documentType.replace(/([A-Z])/g, ' $1').trim()}</div>
                        <DocumentReferences item={c} />
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-[11px] text-slate-500 dark:text-gray-400">Outstanding</span>
                          <span className="font-mono text-xs font-semibold text-emerald-700 dark:text-emerald-400">{fmt(c.outstandingAmount)}</span>
                        </div>
                        {c.dueDate && (
                          <div className="text-[10px] text-slate-400 mt-0.5">Due: {fmtDate(c.dueDate)}</div>
                        )}
                      </div>
                    </label>
                  ))
                )}
              </div>
              {/* Credit total */}
              <div className="border-t border-slate-200 dark:border-slate-700 px-4 py-2.5 bg-slate-50 dark:bg-slate-700/40">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500 dark:text-gray-400">Available credit</span>
                  <span className="font-mono font-bold text-emerald-700 dark:text-emerald-400">{fmt(totalCreditAvailable)}</span>
                </div>
              </div>
            </div>

            {/* ── RIGHT: Debits / Invoices ───────────────────────────── */}
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between bg-white dark:bg-slate-800">
                <h2 className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  {isAR ? 'Invoices / Debit Notes' : 'Bills / Debit Notes'}
                </h2>
                <div className="flex items-center gap-2">
                  <button onClick={handleClearAmounts} className="text-xs text-slate-500 dark:text-gray-400 hover:text-slate-700 px-2 py-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700">
                    Clear
                  </button>
                  <button onClick={handleFifo} disabled={totalCreditAvailable === 0}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-lg transition-colors">
                    <Zap size={12} /> Auto FIFO
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto bg-white dark:bg-slate-800">
                {debits.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                    <FileText size={32} className="mb-2 opacity-30" />
                    <p className="text-sm">No outstanding {isAR ? 'invoices' : 'bills'}</p>
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 z-10">
                      <tr className="bg-slate-50 dark:bg-slate-700/60 border-b border-slate-200 dark:border-slate-700 text-xs text-slate-500 dark:text-gray-400 uppercase tracking-wider">
                        <th className="px-4 py-2.5 text-left font-semibold">Document</th>
                        <th className="px-4 py-2.5 text-left font-semibold">Date</th>
                        <th className="px-4 py-2.5 text-left font-semibold">Due</th>
                        <th className="px-4 py-2.5 text-right font-semibold">Original</th>
                        <th className="px-4 py-2.5 text-right font-semibold">Outstanding</th>
                        <th className="px-4 py-2.5 text-right font-semibold w-36">Allocate</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                      {debits.map(d => {
                        const amount = parseFloat(debitAmts[d.id] || '0') || 0;
                        const overAllocated = amount > d.outstandingAmount + 0.001;
                        const hasAmount = amount > 0;
                        return (
                          <tr key={d.id} className={cn(
                            'hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors',
                            hasAmount && 'bg-emerald-50/50 dark:bg-emerald-900/10',
                          )}>
                            <td className="px-4 py-2.5">
                              <div className="font-mono text-xs font-medium text-blue-700 dark:text-blue-400">{d.documentNumber}</div>
                              <div className="text-[10px] text-slate-400">{d.documentType.replace(/([A-Z])/g, ' $1').trim()}</div>
                              <DocumentReferences item={d} />
                            </td>
                            <td className="px-4 py-2.5 text-xs text-slate-600 dark:text-slate-400 whitespace-nowrap">{fmtDate(d.documentDate)}</td>
                            <td className="px-4 py-2.5 text-xs text-slate-600 dark:text-slate-400 whitespace-nowrap">
                              {d.dueDate ? fmtDate(d.dueDate) : '—'}
                              {d.daysOverdue && d.daysOverdue > 0
                                ? <span className="ml-1 text-red-500 text-[10px]">({d.daysOverdue}d)</span>
                                : null}
                            </td>
                            <td className="px-4 py-2.5 text-right font-mono text-xs text-slate-600 dark:text-slate-400 whitespace-nowrap">{fmt(d.originalAmount)}</td>
                            <td className="px-4 py-2.5 text-right font-mono text-xs font-medium text-slate-900 dark:text-white whitespace-nowrap">{fmt(d.outstandingAmount)}</td>
                            <td className="px-4 py-2.5">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                max={d.outstandingAmount}
                                value={debitAmts[d.id] ?? ''}
                                onChange={e => setDebitAmts(a => ({ ...a, [d.id]: e.target.value }))}
                                placeholder="0.00"
                                className={cn(
                                  'w-full text-right px-2 py-1.5 text-xs font-mono border rounded-lg outline-none focus:ring-2',
                                  overAllocated
                                    ? 'border-red-300 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 focus:ring-red-400'
                                    : hasAmount
                                    ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 focus:ring-emerald-400'
                                    : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-blue-500',
                                )}
                              />
                              {overAllocated && <div className="text-[10px] text-red-600 dark:text-red-400 mt-0.5 text-right">Exceeds outstanding</div>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          {/* ── Footer totals ──────────────────────────────────────────── */}
          <div className="bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 px-6 py-3 shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-8">
                <div className="text-center">
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">Available Credit</p>
                  <p className="font-mono font-bold text-emerald-700 dark:text-emerald-400 text-sm mt-0.5">{fmt(totalCreditAvailable)}</p>
                </div>
                <div className="text-slate-200 dark:text-slate-600 select-none text-lg">−</div>
                <div className="text-center">
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">Total Allocated</p>
                  <p className={cn('font-mono font-bold text-sm mt-0.5', totalAllocated > 0 ? 'text-blue-700 dark:text-blue-400' : 'text-slate-400')}>{fmt(totalAllocated)}</p>
                </div>
                <div className="text-slate-200 dark:text-slate-600 select-none text-lg">=</div>
                <div className="text-center">
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">Remaining</p>
                  <p className={cn('font-mono font-bold text-sm mt-0.5', remaining < -0.001 ? 'text-red-600 dark:text-red-400' : remaining === 0 && totalAllocated > 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-700 dark:text-slate-300')}>{fmt(remaining)}</p>
                </div>
              </div>
              {remaining < -0.001 && (
                <div className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg">
                  <AlertTriangle size={13} /> Over-allocated by {fmt(Math.abs(remaining))}
                </div>
              )}
              {totalAllocated > 0 && remaining >= 0 && (
                <div className="flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2 rounded-lg">
                  <CheckCircle2 size={13} /> {debits.filter(d => (parseFloat(debitAmts[d.id] || '0') || 0) > 0).length} line{debits.filter(d => (parseFloat(debitAmts[d.id] || '0') || 0) > 0).length !== 1 ? 's' : ''} ready
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
